import axiosInstance from 'Utils/axiosInstance';

// The API returns dates in UTC; the hourly series comes without offset, so it is added here.
const parseUtcDate = (value) => {
    if (!value) return null;

    const hasOffset = /([zZ]|[+-]\d{2}:\d{2})$/.test(value);

    return new Date(hasOffset ? value : `${value}Z`);
};

const mapDtoToIncident = (dto) => ({
    ...dto,
    health_status_since: parseUtcDate(dto.health_status_since),
});

const mapDtoToConnectorHealth = (dto) => ({
    ...dto,
    health_status_since: parseUtcDate(dto.health_status_since),
    health_detection_muted_until: parseUtcDate(dto.health_detection_muted_until),
    health_probe_last_at: parseUtcDate(dto.health_probe_last_at),
});

export async function getConnectorsIncidents() {
    const response = await axiosInstance.get('/integrations/connectors/incidents');

    if (!Array.isArray(response?.data)) return [];

    return response.data.map(mapDtoToIncident);
}

export async function getConnectorsHealth({
    query,
    service,
    onlyIncidents,
    onlyWithData,
    orderBy,
    order,
    page,
    pageSize,
}) {
    const response = await axiosInstance.get('/integrations/connectors/health', {
        params: {
            query: query || undefined,
            service: service || undefined,
            only_incidents: onlyIncidents,
            only_with_data: onlyWithData,
            order_by: orderBy,
            order,
            page,
            page_size: pageSize,
        },
    });

    return {
        count: response?.data?.count ?? 0,
        items: (response?.data?.items ?? []).map(mapDtoToConnectorHealth),
    };
}

export async function getConnectorHealth(connectorId) {
    const response = await axiosInstance.get(`/integrations/connectors/${connectorId}/health`);

    if (!response?.data) return null;

    const {
        connector,
        hourly_series: hourlySeries,
        last_attempts: lastAttempts,
        connections_by_status: connectionsByStatus,
    } = response.data;

    const mapAttempt = (item) => ({ ...item, creation_date: parseUtcDate(item.creation_date) });

    return {
        connector: mapDtoToConnectorHealth(connector),
        hourlySeries: hourlySeries.map((item) => ({ ...item, hour: parseUtcDate(item.hour) })),
        lastAttempts: {
            synchronizations: lastAttempts.synchronizations.map(mapAttempt),
            connections: lastAttempts.connections.map(mapAttempt),
        },
        connectionsByStatus: connectionsByStatus ?? null,
    };
}

export async function updateConnectorHealth(
    connectorId,
    { healthStatus, healthStatusMessage, notifyUsers, muteDetectionHours },
) {
    const response = await axiosInstance.put(`/integrations/connectors/${connectorId}/health`, {
        health_status: healthStatus,
        health_status_message: healthStatusMessage?.trim() || null,
        notify_users: notifyUsers,
        mute_detection_hours: muteDetectionHours || null,
    });

    return mapDtoToConnectorHealth(response.data);
}

export async function probeConnectorHealth(connectorId) {
    const response = await axiosInstance.post(`/integrations/connectors/${connectorId}/health/probe`);

    return response?.data;
}

export async function generateConnectorHealthTicket(connectorId) {
    const response = await axiosInstance.post(`/integrations/connectors/${connectorId}/health/ticket`);

    return response?.data;
}

// What support can change of a connector: who can connect it and how it synchronizes.
export async function getConnectorConfiguration(connectorId) {
    const response = await axiosInstance.get(`/integrations/connectors/${connectorId}/configuration`);

    return response.data;
}

// The whole configuration, every field: the ones left out would not keep their value.
export async function updateConnectorConfiguration(connectorId, configuration) {
    const response = await axiosInstance.put(`/integrations/connectors/${connectorId}/configuration`, configuration);

    return response.data;
}
